/** IndexedDB queue for field visits captured without a network connection. */
const DB_NAME = "ream-offline";
const STORE = "visits";

function open() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "id" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(mode, fn) {
  return open().then(
    (db) =>
      new Promise((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        t.oncomplete = () => resolve(req?.result);
        t.onerror = () => reject(t.error);
      })
  );
}

export async function queueVisit(entry) {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  await tx("readwrite", (s) => s.put({ ...entry, id, queued_at: new Date().toISOString() }));
  return id;
}

export async function listQueued() {
  return (await tx("readonly", (s) => s.getAll())) || [];
}

export async function removeQueued(id) {
  return tx("readwrite", (s) => s.delete(id));
}

/** Uploads every queued visit. Returns { sent, failed }. Guarded against concurrent runs. */
let flushing = null;

export async function flushQueue(api) {
  if (flushing) return flushing;
  flushing = (async () => {
    const items = await listQueued();
    let sent = 0;
    let failed = 0;
    for (const item of items) {
      // claim the item first so a parallel run cannot upload it again
      const claimed = await tx("readwrite", (s) => s.get(item.id));
      if (!claimed || claimed.uploading) continue;
      await tx("readwrite", (s) => s.put({ ...claimed, uploading: true }));
      const fd = new FormData();
      (item.files || []).forEach((f) => fd.append("files", f.blob, f.name));
      if (item.latitude != null) {
        fd.append("latitude", item.latitude);
        fd.append("longitude", item.longitude);
        if (item.accuracy != null) fd.append("accuracy", item.accuracy);
      }
      fd.append("notes", item.notes || "");
      fd.append("observation", item.observation || "Normal");
      fd.append("captured_at_client", item.captured_at || "");
      try {
        await api.post(`/parcels/${item.parcel_id}/visits`, fd);
        await removeQueued(item.id);
        sent += 1;
      } catch (e) {
        await tx("readwrite", (s) => s.put({ ...claimed, uploading: false }));
        failed += 1;
      }
    }
    return { sent, failed };
  })();
  try {
    return await flushing;
  } finally {
    flushing = null;
  }
}
